import { AdminAccountsOverview } from '@activepieces/shared';

import { PERCENT_MULTIPLIER } from './constants';

export type OverviewKpiKey =
  | 'activationRate'
  | 'activeAccounts'
  | 'provisionedAccounts'
  | 'enabledFlows'
  | 'tasks'
  | 'mcpExecutions';

// A count moves by a percentage; a rate moves by points. Conflating the two is
// how a rate going from 50% to 60% gets reported as "+20%".
export type OverviewKpiChange =
  | { kind: 'percent'; value: number }
  | { kind: 'points'; value: number };

export type OverviewKpi = {
  key: OverviewKpiKey;
  value: number;
  unit: 'count' | 'percent';
  change: OverviewKpiChange | null;
};

type SeriesPoint = AdminAccountsOverview['series'][number];

const activationRate = (point: SeriesPoint | undefined): number => {
  if (!point || point.provisionedAccounts === 0) {
    return 0;
  }
  return Math.round(
    (point.activeAccounts / point.provisionedAccounts) * PERCENT_MULTIPLIER,
  );
};

const countChange = (
  current: number,
  previous: number | undefined,
): OverviewKpiChange | null => {
  // No baseline, or a zero baseline: any ratio would be meaningless or
  // infinite, so we say nothing rather than print a fake trend.
  if (previous === undefined || previous === 0) {
    return null;
  }
  return {
    kind: 'percent',
    value: Math.round(((current - previous) / previous) * PERCENT_MULTIPLIER),
  };
};

const pointsChange = (
  current: number,
  previous: number | undefined,
): OverviewKpiChange | null =>
  previous === undefined ? null : { kind: 'points', value: current - previous };

export const buildOverviewKpis = (
  overview: AdminAccountsOverview,
): OverviewKpi[] => {
  const current = overview.series.at(-1);
  const previous = overview.series.at(-2);

  const countKpi = (
    key: OverviewKpiKey,
    read: (point: SeriesPoint) => number,
  ): OverviewKpi => {
    const previousValue = previous ? read(previous) : undefined;
    return {
      key,
      value: current ? read(current) : 0,
      unit: 'count',
      change: current ? countChange(read(current), previousValue) : null,
    };
  };

  // A month-to-date total has no fair delta — the table keeps no value for the
  // same day last month — so the comparison lives in the monthly charts.
  const monthToDateKpi = (
    key: OverviewKpiKey,
    read: (point: SeriesPoint) => number,
  ): OverviewKpi => ({
    key,
    value: current ? read(current) : 0,
    unit: 'count',
    change: null,
  });

  const previousRate = previous ? activationRate(previous) : undefined;

  return [
    {
      key: 'activationRate',
      value: activationRate(current),
      unit: 'percent',
      change: current
        ? pointsChange(activationRate(current), previousRate)
        : null,
    },
    countKpi('activeAccounts', (point) => point.activeAccounts),
    countKpi('provisionedAccounts', (point) => point.provisionedAccounts),
    countKpi('enabledFlows', (point) => point.totalEnabledFlows),
    monthToDateKpi('tasks', (point) => point.totalTasks),
    monthToDateKpi('mcpExecutions', (point) => point.totalMcpExecutions),
  ];
};
